import { useState } from "react";
import {
  desktopBlockStyle,
  statisticsPointStyle,
  mobileStatisticsPointStyle,
} from "../styles";

import {
  travelTexts,
  travelIconsNames,
  weddingTexts,
  weddingIconsNames,
} from "../texts/statistics";

interface ContentProps extends MobileMode, OrientationMode, DarkMode {
  purpose: string;
  changeButton: JSX.Element;
}

const ContentBlock = ({
  isMobile,
  isVertical,
  isDark,
  purpose,
  changeButton,
}: ContentProps) => {
  const isHorizontalMobile = isMobile && !isVertical;
  const isVerticalDesktop = !isMobile && isVertical;

  const pointStyle = isMobile
    ? mobileStatisticsPointStyle
    : statisticsPointStyle;

  const imageWidth =
    isMobile || isVerticalDesktop || window.innerWidth < 1536 ? 35 : 50;

  const textSize = isMobile
    ? isHorizontalMobile
      ? "text-xs"
      : window.innerWidth < 321
      ? "text-sm"
      : ""
    : isVerticalDesktop
    ? "md:text-sm lg:text-lg"
    : "md:text-xs lg:text-sm 2xl:text-lg";

  const cols = isHorizontalMobile ? 3 : 1;
  const rows = isHorizontalMobile ? 2 : purpose === "travel" ? 5 : 6;

  const contentList =
    purpose === "travel"
      ? travelTexts
      : isHorizontalMobile
      ? weddingTexts.slice(1)
      : weddingTexts;

  const imageNames =
    purpose === "travel"
      ? travelIconsNames
      : isHorizontalMobile
      ? weddingIconsNames.slice(1)
      : weddingIconsNames;

  const needBold = purpose === "wedding" && !isHorizontalMobile;
  const needPb = needBold && isMobile;

  return (
    <div
      className={
        isMobile
          ? isHorizontalMobile
            ? "flex"
            : "w-full px-4 pt-3 h-[40vh]"
          : isVerticalDesktop && purpose === "travel"
          ? "pt-4 w-3/4"
          : "w-3/4"
      }
    >
      {Array.from({ length: cols }).map((_, col) => (
        <div className={isHorizontalMobile ? "px-4" : ""} key={col}>
          {Array.from({ length: rows }).map((_, row) => {
            const index = col * rows + row;
            const isPb = needPb && !index;
            const isBold = needBold && !index;

            return (
              index < contentList.length && (
                <div
                  key={row}
                  className={`${pointStyle} ${isPb ? "pb-4" : ""} ${
                    isHorizontalMobile ? "h-1/2" : ""
                  }`}
                >
                  <img
                    src={`/statistics/${imageNames[index]}-${
                      isDark ? "dark" : "light"
                    }.png`}
                    alt={imageNames[index]}
                    width={imageWidth}
                  />
                  <p className={`${textSize} ${isBold ? "font-semibold" : ""}`}>
                    {contentList[index]}
                  </p>
                </div>
              )
            );
          })}
          {col === 2 && (
            <div
              className={
                isHorizontalMobile
                  ? "flex justify-end items-end h-1/2"
                  : "hidden"
              }
            >
              {changeButton}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const StatsWidget = ({
  isMobile,
  isVertical,
  isDark,
}: MobileMode & OrientationMode & DarkMode) => {
  const [isWeddingDay, setWeddingDay] = useState(false);
  const [isHover, setHover] = useState(false);

  const isHorizontalMobile = isMobile && !isVertical;
  const isVerticalDesktop = !isMobile && isVertical;

  const verticalDesktopMaxHeight =
    window.innerHeight < 1180
      ? "h-[32vh]"
      : window.innerHeight < 1280
      ? "h-[28vh]"
      : window.innerHeight < 1536
      ? "h-[25vh]"
      : window.innerHeight < 2160
      ? "h-[19vh]"
      : "h-[11vh]";

  const arrowRightPath = `${
    isHover
      ? `/statistics/arrow-hover-right-${isDark ? "dark" : "light"}.png`
      : `/statistics/arrow-right-${isDark ? "dark" : "light"}.png`
  }`;

  const arrowReturnPath = `${
    isHover
      ? `/statistics/arrow-hover-return-${isDark ? "dark" : "light"}.png`
      : `/statistics/arrow-return-${isDark ? "dark" : "light"}.png`
  }`;

  const changeButton = (
    <button
      className="h-min"
      onClick={() => setWeddingDay(!isWeddingDay)}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <img
        src={`${isWeddingDay ? arrowReturnPath : arrowRightPath}`}
        alt="arrow button"
        width={window.innerWidth < 321 ? 40 : 50}
      ></img>
    </button>
  );

  const travelBlock = (
    <ContentBlock
      isMobile={isMobile}
      isVertical={isVertical}
      isDark={isDark}
      purpose="travel"
      changeButton={changeButton}
    />
  );

  const weddingBlock = (
    <ContentBlock
      isMobile={isMobile}
      isVertical={isVertical}
      isDark={isDark}
      purpose="wedding"
      changeButton={changeButton}
    />
  );

  return (
    <div
      className={`flex rounded-xl ${isDark ? "bg-gray-900" : "bg-red-100"}
        ${
          isMobile
            ? isHorizontalMobile
              ? "py-2 h-[67vh]"
              : "flex-col gap-32 w-full"
            : isVerticalDesktop
            ? `px-8 pb-8 w-full 3xl:w-3/5 ${verticalDesktopMaxHeight}`
            : "md:h-[45vh] lg:h-[48vh] pl-8 py-2 w-1/2 3xl:w-[25vw] 2xl:h-auto"
        }`}
    >
      {!isWeddingDay && travelBlock}
      {isWeddingDay && weddingBlock}
      <div
        className={
          isHorizontalMobile
            ? "hidden"
            : isMobile
            ? "flex justify-center pt-2 pb-8"
            : "flex pl-20 items-center pr-4"
        }
      >
        {changeButton}
      </div>
    </div>
  );
};

export default function Statistics({
  isMobile,
  isVertical,
  isDark,
  setContent,
  setModalOpen,
}: PageProps) {
  const isVerticalDesktop = !isMobile && isVertical;

  const isBigScreen = isVerticalDesktop && window.innerHeight > 1535;

  const photo = (
    <img
      className="rounded-xl"
      src={`/statistics/guests${isMobile ? "-mobile" : ""}.jpg`}
      style={{ opacity: isDark ? 0.8 : 1 }}
      alt="guests"
      width={"100%"}
    />
  );

  return (
    <div
      className={
        isMobile
          ? `flex flex-col ${
              window.innerWidth < 321 ? "pt-4 gap-4" : "pt-6 gap-6"
            } items-center justify-between `
          : `${desktopBlockStyle} ${
              isVerticalDesktop ? "sm:pt-8 tablet:pt-20" : "pt-16"
            }`
      }
    >
      <p
        className={`text-center ${
          isMobile
            ? window.innerWidth < 321
              ? "text-xl"
              : "text-2xl"
            : isVerticalDesktop
            ? "text-2xl pb-10"
            : "text-3xl pb-20 pl-2"
        } `}
      >
        Немного статистики
      </p>
      <div
        className={
          isMobile
            ? "flex flex-col items-center w-[90vw]"
            : isVerticalDesktop
            ? "flex flex-col items-center gap-10"
            : "flex md:gap-8 xl:gap-16 2xl:gap-32 3xl:justify-center"
        }
      >
        <StatsWidget
          isMobile={isMobile}
          isVertical={isVertical}
          isDark={isDark}
        />
        <div
          className={
            isMobile
              ? "hidden"
              : `border-double border-8 rounded-3xl 
              ${isDark ? "border-gray-700" : "border-red-200"} 
              ${
                isVerticalDesktop
                  ? "p-2"
                  : "flex 2xl:h-min md:items-center w-1/2 3xl:w-auto p-2"
              }`
          }
        >
          <button
            onClick={() => {
              setContent(photo);
              setModalOpen(true);
            }}
            className="h-min"
          >
            {photo}
          </button>
        </div>
      </div>
      <div
        className={isBigScreen ? "pt-32" : "hidden"}
        style={{ opacity: 0.7 }}
      >
        <img src={`/placeholder-${isDark ? "dark" : "light"}.png`} alt="" />
      </div>
    </div>
  );
}
